// App.js

import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';

import Header from './Components/Header/Header';
import Footer from "./Components/Footer/Footer";
import StatsDashboard from './Components/StatsDashboard/StatsDashboard';
import Login from './Components/Login/Login';
import Profile from './Components/Profile/Profile';
import Calendar from './Components/Calendar/Calendar';
import Insights from './Components/Insights/Insights';
import Goals from './Components/Goals/Goals';
import Friends from './Components/Friends/Friends';
import Leaderboard from './Components/Leaderboard/Leaderboard';
import Badges from './Components/Badges/Badges';
import Blog from './Components/Blog/Blog';
import BlogPost from './Components/Blog/BlogPost';
import BlogWrite from './Components/Blog/BlogWrite';
import MyPosts from './Components/Blog/MyPosts';
import BlogAdmin from './Components/Blog/BlogAdmin';
import AdminDashboard from './Components/Admin/AdminDashboard';
import Welcome from './Components/Welcome/Welcome';

import MotivationalQuoteModal from './Components/MotivationalQuote/MotivationalQuote';

// Quote utilities
import { 
  shouldShowModalOnLogin, 
  markQuoteAsShown 
} from './Utils/quoteManager';

import api from './Services/Api';

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [userData, setUserData] = useState(null);
  const [showQuoteModal, setShowQuoteModal] = useState(false);

  // ----------------------------
  // Check authentication on load
  // ----------------------------
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // ----------------------------
  // Show quote modal on login
  // ----------------------------
  useEffect(() => {
    if (isLoggedIn && shouldShowModalOnLogin()) {
      const timer = setTimeout(() => {
        setShowQuoteModal(true);
        markQuoteAsShown();
      }, 1000); // slight delay for UX

      return () => clearTimeout(timer);
    }
  }, [isLoggedIn]);

  // ----------------------------
  // Auth verification
  // ----------------------------
  const checkAuthStatus = async () => {
    const token = localStorage.getItem('token');

    if (!token) {
      setIsLoggedIn(false);
      setCheckingAuth(false);
      return;
    }

    try {
      const userResponse = await api.get('profile/');
      setUserData(userResponse.data);
      setIsLoggedIn(true);
    } catch (err) {
      console.error('Auth check failed:', err);

      if (err.response?.status === 401) {
        localStorage.clear();
        setIsLoggedIn(false);
      } else {
        // fallback (offline/server issue)
        setIsLoggedIn(true);
        setUserData({
          username: localStorage.getItem('username') || 'User'
        });
      }
    } finally {
      setCheckingAuth(false);
    }
  };

  // ----------------------------
  // Login handler
  // ----------------------------
  const handleLogin = (userDataFromServer) => {
    setUserData(userDataFromServer);
    setIsLoggedIn(true);
  };

  // ----------------------------
  // Logout handler
  // ----------------------------
  const handleLogout = () => {
    localStorage.clear();
    setUserData(null);
    setIsLoggedIn(false);
  };

  // ----------------------------
  // Render
  // ----------------------------
  
  // Check if current path is admin (for hiding header/footer)
  const isAdminUser = userData?.is_staff || userData?.is_superuser;
  
  return (
    <Router>
      <AppContent 
        isLoggedIn={isLoggedIn}
        checkingAuth={checkingAuth}
        userData={userData}
        isAdminUser={isAdminUser}
        handleLogin={handleLogin}
        handleLogout={handleLogout}
        showQuoteModal={showQuoteModal}
        setShowQuoteModal={setShowQuoteModal}
      />
    </Router>
  );
}

// Separate component to use useLocation inside Router
function AppContent({ 
  isLoggedIn, 
  checkingAuth, 
  userData, 
  isAdminUser,
  handleLogin, 
  handleLogout, 
  showQuoteModal, 
  setShowQuoteModal 
}) {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  
  // Admin routes get a clean layout without header/footer
  if (isAdminRoute && isLoggedIn && isAdminUser) {
    return (
      <div className="App">
        <Routes>
          <Route
            path="/admin"
            element={<AdminDashboard userData={userData} onLogout={handleLogout} />}
          />
        </Routes>
      </div>
    );
  }

  return (
    <div className="App">

      <Header 
        onLogout={handleLogout} 
        isLoggedIn={isLoggedIn} 
        userData={userData} 
      />

      {checkingAuth ? (
        <main>
          <div className="loading-screen">
            <div className="spinner"></div>
            <p>Loading your application...</p>
          </div>
          </main>
        ) : (
          <main>
            <Routes>

              {/* PUBLIC WELCOME PAGE - No auth required */}
              <Route path="/welcome" element={<Welcome />} />
              
              {/* ROOT - Redirect to welcome or dashboard */}
              <Route 
                path="/" 
                element={
                  isLoggedIn ? <Navigate to="/dashboard" replace /> : <Navigate to="/welcome" replace />
                } 
              />

              {/* LOGIN */}
              <Route
                path="/login"
                element={
                  isLoggedIn ? (
                    (userData?.is_staff || userData?.is_superuser) 
                      ? <Navigate to="/admin" replace />
                      : <Navigate to="/dashboard" replace />
                  ) : (
                    <Login onLogin={handleLogin} />
                  )
                }
              />

              {/* ADMIN DASHBOARD - Staff/Superuser only */}
              <Route
                path="/admin"
                element={
                  isLoggedIn ? (
                    (userData?.is_staff || userData?.is_superuser) ? (
                      <AdminDashboard userData={userData} onLogout={handleLogout} />
                    ) : (
                      <Navigate to="/dashboard" replace />
                    )
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />

              {/* DASHBOARD */}
              <Route
                path="/dashboard"
                element={
                  isLoggedIn ? (
                    <StatsDashboard userData={userData} />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />

              {/* ROOT */}
              <Route
                path="/"
                element={
                  isLoggedIn ? (
                    (userData?.is_staff || userData?.is_superuser) 
                      ? <Navigate to="/admin" replace />
                      : <Navigate to="/dashboard" replace />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />

              {/* PROFILE */}
              <Route
                path="/profile"
                element={
                  isLoggedIn ? (
                    <Profile userData={userData} onLogout={handleLogout} />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />

              {/* CALENDAR */}
              <Route
                path="/calendar"
                element={
                  isLoggedIn ? (
                    <Calendar userData={userData} />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />

              {/* INSIGHTS */}
              <Route
                path="/insights"
                element={
                  isLoggedIn ? (
                    <Insights userData={userData} />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />

              {/* GOALS */}
              <Route
                path="/goals"
                element={
                  isLoggedIn ? (
                    <Goals userData={userData} />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />

              {/* FRIENDS */}
              <Route
                path="/friends"
                element={
                  isLoggedIn ? (
                    <Friends userData={userData} />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />

              {/* LEADERBOARD */}
              <Route
                path="/leaderboard"
                element={
                  isLoggedIn ? (
                    <Leaderboard userData={userData} />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />

              {/* BADGES */}
              <Route
                path="/badges"
                element={
                  isLoggedIn ? (
                    <Badges userData={userData} />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />

              {/* BLOG */}
              <Route
                path="/blog"
                element={
                  isLoggedIn ? (
                    <Blog userData={userData} />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/blog/write"
                element={
                  isLoggedIn ? (
                    <BlogWrite />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/blog/edit/:postId"
                element={
                  isLoggedIn ? (
                    <BlogWrite />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/blog/my-posts"
                element={
                  isLoggedIn ? (
                    <MyPosts />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/blog/admin"
                element={
                  isLoggedIn ? (
                    <BlogAdmin userData={userData} />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/blog/:slug"
                element={
                  isLoggedIn ? (
                    <BlogPost />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />

              {/* 404 */}
              <Route
                path="*"
                element={
                  <div style={{ padding: '2rem', textAlign: 'center' }}>
                    <h2>404 - Page Not Found</h2>
                    <p>The page you're looking for doesn't exist.</p>
                  </div>
                }
              />

            </Routes>
          </main>
        )}

        {/* MOTIVATIONAL QUOTE MODAL */}
        {showQuoteModal && (
          <MotivationalQuoteModal
            autoClose={5000}
            onClose={() => setShowQuoteModal(false)}
          />
        )}

        <Footer />

      </div>
  );
}

export default App;
